/** *******************************************************************************************************************
 * Copyright (c) 2020 - Fundação CERTI
 * All rights reserved.
 ******************************************************************************************************************* */

import React, {
  useCallback, forwardRef, useRef, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import MaterialTable from 'material-table';
import * as firebase from 'firebase/app';

import AddIcon from '@material-ui/icons/Add';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';
import { Snackbar, CircularProgress } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { createCompany, updateCompany } from '../../services/companies';

import Button from '../Button';
import { getUserRoles } from '../userRoles';
import { rolesType } from '../utils/constants';
import useStyles from './styles';

const tableIcons = {
  Add: forwardRef((props, ref) => (
    <Button ref={ref} {...props} icon={AddIcon}>
      NOVA EMPRESA
    </Button>
  )),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const CompaniesTable = ({ companiesData, setCompaniesData, handleShowModal, isLoading }) => {
  const [roles, setRoles] = useState(null);
  const [notification, setNotification] = useState(null);
  const tableRef = useRef(null);
  const classes = useStyles();

  useEffect(() => {
    const getRoles = async () => {
      const data = await getUserRoles(firebase.auth().currentUser.uid);
      setRoles(data);
    };
    getRoles();
  }, []);

  const handleClickSamplers = useCallback(
    (event) => {
      const { value } = event.currentTarget;
      handleShowModal(value);
    },
    [handleShowModal],
  );

  const onRowAdd = useCallback(
    (newData) => new Promise((resolve) => {
      Object.keys(newData).forEach((key) => {
        if (typeof newData[key] === 'string') {
          newData[key] = newData[key].trim();
        }
      });

      if (!roles.includes(rolesType.admin) && !roles.includes(rolesType.technician)) {
        setNotification({ label: 'Você não tem permissão para isso', type: 'error' });
        resolve();
        return;
      }

      createCompany(newData).then((result) => {
        if (!result) {
          resolve();
          return;
        }
        const page = tableRef.current.dataManager.currentPage;
        const { pageSize } = tableRef.current.dataManager;
        setCompaniesData((prev) => {
          const newCompanies = [...prev];
          newCompanies.splice(page * pageSize, 0, result);

          setNotification({ label: 'Empresa criada', type: 'success' });

          return newCompanies;
        });
        resolve();
      });
    }),
    [setCompaniesData, tableRef, roles],
  );

  const onRowUpdate = useCallback(
    (newData, oldData) => new Promise((resolve) => {
      Object.keys(newData).forEach((key) => {
        if (typeof newData[key] === 'string') {
          newData[key] = newData[key].trim();
        }
      });

      if (!roles.includes(rolesType.admin) && !roles.includes(rolesType.technician)) {
        setNotification({ label: 'Você não tem permissão para isso', type: 'error' });
        resolve();
        return;
      }

      updateCompany(oldData.id, newData).then((result) => {
        if (!result) {
          resolve();
          return;
        }

        setCompaniesData((prev) => {
          const dataUpdate = [...prev];
          const index = oldData.tableData.id;
          dataUpdate[index] = newData;
          return dataUpdate;
        });
        resolve();
      });
    }),
    [setCompaniesData, roles],
  );

  // const onRowDelete = useCallback(
  //   async (oldData) => new Promise((resolve) => {
  //     deleteCompany(oldData.id).then((result) => {
  //       if (!result) {
  //         return;
  //       }
  //       setCompaniesData((prevState) => {
  //         const data = prevState;
  //         data.splice(data.indexOf(oldData), 1);
  //         return data;
  //       });
  //     });
  //   }),
  //   [setCompaniesData],
  // );

  const handleCloseNotification = useCallback(() => {
    setNotification(null);
  }, [setNotification]);

  return (
    <>
      {isLoading ? (
        <CircularProgress />
      ) : (
          <MaterialTable
            tableRef={tableRef}
            icons={tableIcons}
            style={{ boxShadow: 'none' }}
            columns={companiesData.columns.map((column) =>
              column.field !== 'samplersCount'
                ? column
                : {
                  ...column,
                  render: (props) => {
                    if (props) {
                      const { id, samplersCount } = props;
                      return (id && <Button className={classes.link} value={id} onClick={handleClickSamplers}>
                        {`${samplersCount != null ? samplersCount : 0} (editar)`}
                      </Button>
                      );
                    }
                  },
                })}

            data={companiesData.data}

            options={{
              searchFieldAlignment: 'left',
              showTitle: false,
              actionsColumnIndex: companiesData.columns.length,
              addRowPosition: 'first',
            }}
            localization={{
              header: {
                actions: 'Ações',
              },
              body: {
                editTooltip: 'Editar',
                addTooltip: 'Adicionar',
                editRow: {
                  cancelTooltip: 'Cancelar',
                  saveTooltip: 'Salvar',
                }
              },
              toolbar: {
                searchPlaceholder: "Buscar",
                searchTooltip: "Buscar",
              },
              pagination: {
                labelRowsSelect: "itens por página",
                firstTooltip: "Primeira página",
                previousTooltip: "Página anterior",
                nextTooltip: "Próxima página",
                lastTooltip: "Última página"
              }
            }}
            editable={{
              onRowAdd,
              onRowUpdate,
              // onRowDelete,
            }}
          />
        )}
      <Snackbar open={!!notification} autoHideDuration={6000} onClose={handleCloseNotification}>
        <MuiAlert
          onClose={handleCloseNotification}
          elevation={6}
          variant="filled"
          severity={notification?.type || 'success'}
        >
          {notification?.label}

        </MuiAlert>
      </Snackbar>
    </>
  );
};

CompaniesTable.propTypes = {
  companiesData: PropTypes.shape({
    columns: PropTypes.shape(),
    data: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  setCompaniesData: PropTypes.func.isRequired,
};

export default CompaniesTable;
